<template>
    <div class="sidebar">
        <div class="sidebar-header">
            <h3>Groups</h3>
            <div class="sidebar-actions">
                <button v-if="!isSorted" @click="$emit('sort-groups')" :tabindex="isSearching ? -1 : 0" class="toggle-button" title="Sort Alphabetically">
                    <Icon name="sort-alpha-down" size="xl" color="var(--primary-700)" hover="var(--grayscale-600)"></Icon>
                </button>
                <button v-if="isSorted" @click="$emit('reset-sort')" :tabindex="isSearching ? -1 : 0" class="toggle-button" title="Reset Sort">
                    <Icon name="fa-sharp fa-solid fa-rotate-left" size="xl" color="var(--primary-700)" hover="var(--grayscale-600)"></Icon>
                </button>
            </div>
        </div>

        <div class="sidebar-list">
            <button
                v-for="section in filteredSections"
                :key="section.id"
                class="sidebar-item"
                :class="{ 'active': activeSectionId === section.id && !isSearching }"
                @click="$emit('set-active-section', section.id)"
                :tabindex="isSearching ? -1 : 0"
            >
                {{ section.title }}
            </button>
            <div v-if="filteredSections.length === 0" class="no-results">
                <p>No groups found.</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import Icon from '@common/icon/Icon.vue';

defineProps({
    filteredSections: {
        type: Array,
        required: true
    },
    activeSectionId: {
        type: String,
        default: null
    },
    isSorted: {
        type: Boolean,
        default: false
    },
    isSearching: {
        type: Boolean,
        default: false
    }
});

defineEmits(['sort-groups', 'reset-sort', 'set-active-section']);
</script>

<style scoped>
.sidebar {
    width: 300px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    position: sticky;
    top: var(--space-6);
    background: var(--grayscale-50);
    //border: 1px solid var(--grayscale-100);
    border-radius: var(--radius-lg);
    padding-bottom: var(--space-2);
}

.sidebar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--space-4) var(--space-5);
    margin-bottom: var(--space-1);
}

.sidebar-header h3 {
    margin: 0;
    font-size: var(--text-size-lg);
    letter-spacing: 0.02rem;
    color: var(--grayscale-700);
    font-weight: var(--text-weight-semibold);
}

.sidebar-actions {
    display: flex;
    justify-content: space-between;
}

button {
    border: none;
    background: none;
    cursor: pointer;
}

.toggle-button {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-size-sm);
    color: var(--grayscale-600);
}

.toggle-button:hover {
    color: var(--primary-700);
}

.sidebar-list {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 200px);
    overflow-y: auto;
    padding-bottom: var(--space-4);
}

.sidebar-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--space-3) var(--space-5);
    text-align: left;
    font-size: var(--text-size-md);
    font-weight: var(--text-weight-medium);
    color: var(--grayscale-700);
    transition: all 0.2s;
    border-left: 3px solid transparent;
}

.sidebar-item:hover,
.sidebar-item.active {
    background-color: var(--grayscale-100);
}

.sidebar-item.active {
    color: var(--primary-700);
    font-weight: var(--text-weight-bold);
    border-left: 3px solid var(--primary-500);
}

.no-results {
    text-align: center;
    padding: 2rem 0;
    color: var(--grayscale-500);
}
</style>
